import { useLazyQuery } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Alert, Box, Grid, ScrollBox, Table } from '~/components/blocks';
import {
  GetSettingsPractitionersDocument,
  GetSettingsPractitionersQuery,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { useFeatureFlag } from '~/hooks/use-feature-flag';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { usePreviousValue } from '~/hooks/use-previous-value';
import {
  editDrawerState,
  newDrawerState,
  pageInfoState,
} from '~/state/settings_practitioners/atoms';

import { Practitioner } from './Practitioner';
import { PractitionerPagination } from './PractitionerPagination';

const getOrganizationId = (data: GetSettingsPractitionersQuery) => {
  return getMe(data)?.organization.id || null;
};

const getPrivilege = (data: GetSettingsPractitionersQuery) => {
  return getMe(data)?.organization.privilege || false;
};

const getTotalPage = (data: GetSettingsPractitionersQuery) => {
  return getMe(data)?.organization.practitioners.pagesCount || 1;
};

const formatPractitioners = (data: GetSettingsPractitionersQuery) => {
  const me = getMe(data);

  return me ? me.organization.practitioners.nodes.map((node) => node) : [];
};

export const PractitionerList = () => {
  const theme = useTheme();

  const [pageInfo, setPageInfo] = useAtom(pageInfoState);
  const setNewDrawerState = useSetAtom(newDrawerState);
  const setEditDrawerState = useSetAtom(editDrawerState);
  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);
  const [getPractitioners, { data, loading }] = useLazyQuery(GetSettingsPractitionersDocument, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const prevData = usePreviousValue(data);

  useEffect(() => {
    if (!data) return;
    setPageInfo((state) => ({
      ...state,
      totalCount: getMe(data)?.organization.practitioners.nodesCount || 0,
      totalPage: getTotalPage(data),
    }));
    setNewDrawerState((state) => ({
      ...state,
      isPrivilege: getPrivilege(data),
      organizationId: getOrganizationId(data),
    }));
    scrollRef.current?.toTop();
  }, [data, setNewDrawerState, setPageInfo]);
  const currentData = data || prevData;
  const practitioners = useMemo(
    () => (currentData ? formatPractitioners(currentData) : []),
    [currentData],
  );
  const isPrivilege = useMemo(() => (data ? getPrivilege(data) : false), [data]);
  const { verifyRole } = usePractitionerRole();
  const displayTwoFactorAuthentication = useFeatureFlag('Issue13782-password_policy');
  const handleOpenEditDrawer = useCallback(
    (practitionerId: string) => {
      setNewDrawerState((_state) => ({ ..._state, isOpen: false }));
      setEditDrawerState({ isOpen: true, isPrivilege, practitionerId });
    },
    [isPrivilege, setEditDrawerState, setNewDrawerState],
  );

  useEffect(() => {
    getPractitioners({
      variables: {
        page: pageInfo.page,
        perPage: pageInfo.perPage,
      },
    });
  }, [getPractitioners, pageInfo.page, pageInfo.perPage]);

  return (
    <Box height="100%" overflow="auto" mt={theme.space.xl}>
      <Grid
        height="100%"
        gridTemplateRows="1fr min-content"
        gridTemplateColumns="1fr"
        overflow="auto"
      >
        <ScrollBox ref={scrollRef} loading={loading}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>氏名</Table.Th>
                <Table.Th>ユーザーID</Table.Th>
                <Table.Th>権限</Table.Th>
                {displayTwoFactorAuthentication && <Table.Th>二要素認証</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {practitioners.map((practitioner, idx) => {
                return (
                  <Practitioner
                    key={idx}
                    id={practitioner.id}
                    familyName={practitioner.familyName}
                    givenName={practitioner.givenName}
                    phoneticFamilyName={practitioner.phoneticFamilyName}
                    phoneticGivenName={practitioner.phoneticGivenName}
                    username={practitioner.authentication.username}
                    roles={practitioner.roles}
                    twoFactorAuthenticationConfigured={
                      practitioner.twoFactorAuthenticationConfigured
                    }
                    displayTwoFactorAuthentication={displayTwoFactorAuthentication}
                    editable={verifyRole('admin')}
                    onOpenEditDrawer={handleOpenEditDrawer}
                  />
                );
              })}
            </Table.Tbody>
          </Table>
          {!loading && practitioners.length === 0 && (
            <Alert status="info" mt={theme.space.xl}>
              スタッフは登録されていません
            </Alert>
          )}
        </ScrollBox>
        <Box paddingY={theme.space.l}>{practitioners.length > 0 && <PractitionerPagination />}</Box>
      </Grid>
    </Box>
  );
};
